<template>
  <v-dialog :value="value" max-width="900" scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title>
        Form Preview
        <v-spacer />
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-4" style="max-height: 70vh; overflow-y: auto;">
        <div v-if="resolving" class="text-center py-6">
          <v-progress-circular indeterminate size="32" width="2" />
          <div class="text-caption grey--text mt-2">Resolving fragments...</div>
        </div>
        <schema-field
          v-else-if="hasFields"
          :fields="displaySchema.fields"
          :fieldsets="displaySchema.fieldsets"
          :value="previewData"
          :root-value="previewData"
          :locales="locales"
          :entity-id="previewEntityId"
          @input="previewData = $event"
        />
        <div v-else class="text-center grey--text py-6">
          No fields to preview.
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="previewData = {}">Reset</v-btn>
        <v-btn text @click="$emit('input', false)">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { cloneDeep } from 'lodash'
import { API } from '@/utils/api'
import SchemaField from '../../items/item-form/SchemaField.vue'

export default {
  components: {
    SchemaField,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    schema: {
      type: Object,
      default: () => ({}),
    },
    locales: {
      type: Array,
      default: () => [],
    },
    allTypes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      previewData: {},
      resolvedSchema: null,
      resolving: false,
      fragmentCache: {},
    }
  },
  computed: {
    displaySchema() {
      return this.resolvedSchema || this.schema
    },
    hasFields() {
      return this.displaySchema && this.displaySchema.fields && this.displaySchema.fields.length > 0
    },
    previewEntityId() {
      const entities = this.$store.state.auth?.entities || []
      return entities.length ? entities[0].entity_id : null
    },
    allTypesBySlug() {
      const map = {}
      for (const t of this.allTypes) {
        if (t?.slug) {
          map[t.slug] = t
        }
      }
      return map
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.previewData = {}
        this.resolveRefs()
      }
    },
  },
  methods: {
    async resolveRefs() {
      if (!Array.isArray(this.schema?.fields)) {
        this.resolvedSchema = null
        return
      }
      if (!this.schema.fields.some(f => this.fieldHasRef(f))) {
        this.resolvedSchema = null
        return
      }
      this.resolving = true
      try {
        const resolved = cloneDeep(this.schema)
        resolved.fields = await this.resolveFieldRefs(resolved.fields)
        this.resolvedSchema = resolved
      } catch (e) {
        this.resolvedSchema = null
      } finally {
        this.resolving = false
      }
    },
    fieldHasRef(field) {
      if (field.$ref) {
        return true
      }
      if (Array.isArray(field.schema)) {
        return field.schema.some(f => this.fieldHasRef(f))
      }
      return false
    },
    async resolveFieldRefs(fields) {
      const out = []
      for (const field of fields) {
        // fragment-поле → разворачиваем в поля целевого типа (с рекурсивным резолвом их $ref)
        if (field.type === 'fragment' && field.$ref) {
          const frag = await this.fetchFragmentFields(field.$ref)
          if (frag && frag.length) {
            const resolved = await this.resolveFieldRefs(frag)
            out.push(...resolved)
          }
          continue
        }
        // object/array с $ref → подкладываем схему фрагмента
        if (field.$ref && (field.type === 'object' || field.type === 'array')) {
          const frag = await this.fetchFragmentFields(field.$ref)
          if (frag) {
            field.schema = frag
          }
        }
        // рекурсия во вложенные inline-схемы (включая только что подложенные)
        if (Array.isArray(field.schema)) {
          field.schema = await this.resolveFieldRefs(field.schema)
        }
        out.push(field)
      }
      return out
    },
    async fetchFragmentFields(slug) {
      // Кеш-хит — отдаём копию, чтобы потребители не мутировали закешированное.
      if (Object.prototype.hasOwnProperty.call(this.fragmentCache, slug)) {
        return cloneDeep(this.fragmentCache[slug])
      }

      let typeId = this.allTypesBySlug[slug]?.type_id
      if (!typeId) {
        // Фолбэк: слаг не нашёлся в allTypes — спросим бэкенд.
        try {
          const tr = await API.call('/cms/type/get.json', { slug })
          typeId = (tr?.data?.item || tr?.data)?.type_id
        } catch (e) {
          return null
        }
      }
      if (!typeId) {
        return null
      }
      try {
        const sr = await API.call('/cms/type-schema/get.json', {
          type_id: typeId,
          current_only: true,
          resolve: false,
        })
        const raw = sr?.data?.item?.schema
        if (!raw) {
          return null
        }
        const refSchema = typeof raw === 'string' ? JSON.parse(raw) : raw
        if (!Array.isArray(refSchema?.fields)) {
          return null
        }
        this.fragmentCache[slug] = refSchema.fields
        return cloneDeep(refSchema.fields)
      } catch (e) {
        return null
      }
    },
  },
}
</script>
